<template>
  <!-- 第三屏：地图独立组件（通过type控制显示） -->
  <section class="fullpage-section section-map" ref="sectionMap">
    <div class="map-container">
      <!-- 热线电话图片 -->
      <div class="hotline-container">
        <img src="../store/new/hotLine.png" alt="热线电话" class="hotline-img">
      </div>

      <!-- 图片容器（仅根据type显示对应图片，无鼠标切换） -->
      <div class="image-switch-container" ref="imageContainer">
        <!-- 步骤图（type=1时显示） -->
        <img 
          src="../store/new/steps.png" 
          alt="步骤图" 
          class="switch-image steps-img" 
          :style="{ opacity: stepsOpacity }"
        >
        <!-- 地图图（type=2时显示） -->
        <img 
          src="../store/new/map.png" 
          alt="地图图" 
          class="switch-image map-img" 
          :style="{ opacity: mapOpacity }"
        >

        <!-- 地图坐标点 + 商店列表（仅type=2时渲染） -->
        <div class="map-local-po" v-if="mapOpacity === 1">
          <!-- 坐标点1 -->
          <img 
            src="https://youke1.picui.cn/s1/2025/08/27/68af0d0787e13.png" 
            class="icon-pos"
            :style="getPositionStyle('1')"
            :class="{ active: activePos === '1' }" 
            @click="posClick('1', $event)" 
            alt="坐标1"
          >
          <!-- 坐标点2 -->
          <img 
            src="https://youke1.picui.cn/s1/2025/08/27/68af0d0787e13.png" 
            class="icon-pos"
            :style="getPositionStyle('2')"
            :class="{ active: activePos === '2' }" 
            @click="posClick('2', $event)" 
            alt="坐标2"
          >
          <!-- 坐标点3 -->
          <img 
            src="https://youke1.picui.cn/s1/2025/08/27/68af0d0787e13.png" 
            class="icon-pos"
            :style="getPositionStyle('3')"
            :class="{ active: activePos === '3' }" 
            @click="posClick('3', $event)" 
            alt="坐标3"
          >

          <!-- 商店列表弹窗 -->
          <div class="shop-list" :style="shopListStyle">
            <div class="shop-list-header">
              <span class="close-btn" @click="closeShopList">×</span>
            </div>
            <div class="shop-item" v-for="(item, index) in shopList" :key="index">
              <div class="shop-title">{{ item.title }}</div>
              <div class="shop-address">{{ item.local }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watchEffect, computed } from 'vue'

/** ---------------- 1. 接收外部传入的type参数 ---------------- **/
const props = defineProps({
  type: {
    type: Number,
    required: true,
    validator: (val) => [1, 2].includes(val) // 仅允许1（步骤图）或2（地图）
  }
})

/** ---------------- 2. 地图组件内部数据（移除自动切换相关变量） ---------------- **/
// 商店列表数据
const shopList = ref([
  { title: "上海张江店", local: "上海市浦东新区张江高科技园区博云路2号" },
  { title: "上海陆家嘴店", local: "上海市浦东新区陆家嘴环路1000号" },
  { title: "上海静安店", local: "上海市静安区南京西路1788号" },
])

// 坐标点位置配置（使用百分比，相对于地图容器）
const posConfig = {
  '1': { top: '58%', left: '52%' },
  '2': { top: '62%', left: '59%' },
  '3': { top: '50%', left: '55%' }
}

// 核心：根据type计算图片透明度（替代原自动切换逻辑）
const stepsOpacity = computed(() => props.type === 1 ? 1 : 0)
const mapOpacity = computed(() => props.type === 2 ? 1 : 0)

// 地图交互相关数据
const activePos = ref(null)
const shopListStyle = ref({ display: 'none', zIndex: 10 })
const imageContainer = ref(null)
const containerSize = ref({ width: 0, height: 0 })

/** ---------------- 3. 响应式位置计算（逻辑保留） ---------------- **/
// 获取坐标点样式（响应式）
const getPositionStyle = (value) => {
  const pos = posConfig[value]
  return {
    top: pos.top,
    left: pos.left,
    position: 'absolute'
  }
}

// 监听容器尺寸变化，更新商店列表位置
watchEffect(() => {
  if (imageContainer.value) {
    containerSize.value.width = imageContainer.value.offsetWidth
    containerSize.value.height = imageContainer.value.offsetHeight
  }
})

/** ---------------- 4. 地图交互方法（保留核心交互，移除切换方法） ---------------- **/
// 坐标点点击事件
const posClick = (value, event) => {
  event.stopPropagation()
  const pos = posConfig[value]
  if (!pos) return

  // 根据容器尺寸计算商店列表位置
  shopListStyle.value = {
    ...shopListStyle.value,
    top: pos.top,
    left: `calc(${pos.left} - 320px)`, // 相对于坐标点左侧定位
    display: 'block'
  }
  activePos.value = value
}

// 关闭商店列表
const closeShopList = () => {
  shopListStyle.value.display = 'none'
  activePos.value = null
}

// 全局点击关闭商店列表
const handleGlobalClick = (event) => {
  const isClickPos = event.target.classList.contains('icon-pos')
  const isClickShopList = event.target.closest('.shop-list')
  if (!isClickPos && !isClickShopList) closeShopList()
}

/** ---------------- 5. 组件生命周期（移除定时器相关逻辑） ---------------- **/
onMounted(() => {
  // 仅保留全局点击事件和窗口resize监听
  document.addEventListener('click', handleGlobalClick)
  window.addEventListener('resize', () => {
    if (imageContainer.value) {
      containerSize.value.width = imageContainer.value.offsetWidth
      containerSize.value.height = imageContainer.value.offsetHeight
    }
  })
})

onBeforeUnmount(() => {
  // 仅清理全局事件监听
  document.removeEventListener('click', handleGlobalClick)
  window.removeEventListener('resize', () => {})
})
</script>

<style scoped>
/* 样式部分无修改，保持原布局和响应式逻辑 */
.section-map {
  background: url("../store/new/bg_shop_1500p.jpg") no-repeat;
  background-size: 100% 100%;
  padding: 1px;
  min-height: 900px;
  height: 100vh;
  scroll-snap-align: start;
  display: flex;
  justify-content: center;
  align-items: center;
}

.map-container {
  display: flex;
  padding: 20px;
  width: 100%;
  height: 80%;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap; /* 小屏幕自动换行 */
}

.hotline-container {
  width: 40%;
  min-width: 280px; /* 最小宽度，避免过小 */
  display: flex;
  align-items: center;
  justify-content: center;
}

.hotline-img {
  max-width: 80%;
  height: auto;
}

.image-switch-container {
  width: 60%;
  min-width: 280px; /* 最小宽度，避免过小 */
  position: relative;
  height: 600px;
  cursor: default;
}

/* 响应式调整图片容器高度 */
@media (max-width: 1200px) {
  .image-switch-container {
    height: 500px;
  }
}

@media (max-width: 992px) {
  .image-switch-container {
    height: 400px;
  }
}

@media (max-width: 768px) {
  .image-switch-container {
    height: 300px;
    width: 100%; /* 小屏幕占满宽度 */
  }
  .hotline-container {
    width: 100%; /* 小屏幕占满宽度 */
    margin-bottom: 20px;
  }
}

.switch-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: opacity 0.5s;
}

.map-local-po {
  height: 100%;
  width: 100%;
  position: relative;
}

.icon-pos {
  height: 40px;
  width: 30px;
  cursor: pointer;
  z-index: 20;
  transition: transform 0.2s;
}

.icon-pos.active {
  filter: brightness(1.2);
  transform: scale(1.1);
}

/* 商店列表样式 */
.shop-list {
  width: 300px;
  max-height: 500px;
  background: #fff;
  border-radius: 5px;
  position: absolute;
  overflow-y: auto;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
}

/* 小屏幕调整商店列表大小 */
@media (max-width: 768px) {
  .shop-list {
    width: 250px;
    max-height: 300px;
  }
}

.shop-list-header {
  padding: 8px 15px;
  text-align: right;
  border-bottom: 1px solid #f5f5f5;
}

.close-btn {
  font-size: 18px;
  color: #666;
  cursor: pointer;
}

.shop-item {
  padding: 12px 15px;
  border-bottom: 1px solid #f0f0f0;
}

.shop-title {
  font-weight: 600;
  color: #333;
}

.shop-address {
  color: #666;
  font-size: 14px;
}
</style>